import React from "react";
import { makeGETRequestOnUserService } from "../../Api/api";
import toastr from "toastr";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Button, TextField, Box, Grid, Card, CardContent, Divider, Stack, Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FilterField, ColumnDef, RazorpayFilterDialogProps } from "./interfaces";

function toUnixTimestamp(dateStr: string): number | undefined {
    if (!dateStr) return undefined;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
    return Math.floor(date.getTime() / 1000);
}

function getNestedValue(obj: any, path: string): any {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function formatAmount(amount: number | null): string {
    if (amount == null) return "—"; return ("₹" + (amount / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 }));
}

function formatTimestamp(unix: number): string {
    return new Date(unix * 1000).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export const ORDERS_FILTER_FIELDS: FilterField[] = [
    { key: "from", label: "From Date", type: "datetime-local" },
    { key: "to", label: "To Date", type: "datetime-local" },
    { key: "count", label: "Count", type: "number", helperText: "Maximum value is 100", max: 100 },
    { key: "skip", label: "Skip", type: "number", helperText: "Number of orders to skip" },
    { key: "receipt", label: "Receipt", type: "text", helperText: "Filter by receipt ID" },
];

export const ORDERS_COLUMNS: ColumnDef[] = [
    { label: "Order ID", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.id}</Box> },
    {
        label: "Status",
        render: (row) => (
            <Chip
                label={row.status}
                size="small"
                sx={{
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: row.status === "paid" ? "#e6f4ea" : row.status === "attempted" ? "#fff3e0" : "#f5f5f5",
                    color: row.status === "paid" ? "#1e7e34" : row.status === "attempted" ? "#e65100" : "#555",
                }}
            />
        ),
    },
    { label: "Amount", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontWeight: 500 }}>{formatAmount(row.amount)}</Typography> },
    { label: "Amount Paid", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatAmount(row.amount_paid)}</Typography> },
    { label: "Amount Due", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatAmount(row.amount_due)}</Typography> },
    { label: "Currency", render: (row) => <Typography variant="body2">{row.currency}</Typography> },
    { label: "Attempts", render: (row) => <Typography variant="body2">{row.attempts}</Typography> },
    { label: "Receipt", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.receipt}</Box> },
    { label: "Customer Name", render: (row) => <Typography variant="body2">{row.notes?.name ?? "—"}</Typography> },
    { label: "Email", render: (row) => <Typography variant="body2">{row.notes?.email ?? "—"}</Typography> },
    { label: "Contact", render: (row) => <Typography variant="body2">{row.notes?.contact ?? "—"}</Typography> },
    { label: "ISIN", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.notes?.isin ?? "—"}</Box> },
    { label: "Security Name", render: (row) => <Typography variant="body2" sx={{ minWidth: 220, display: "inline-block" }}>{row.notes?.security_name ?? "—"}</Typography> },
    { label: "Internal Order ID", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.notes?.rfq_internal_order_number ?? "—"}</Box> },
    { label: "Created At", render: (row) => <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.created_at)}</Typography> },
];

export const SETTLEMENTS_FILTER_FIELDS: FilterField[] = [
    { key: "from", label: "From Date", type: "datetime-local" },
    { key: "to", label: "To Date", type: "datetime-local" },
    { key: "count", label: "Count", type: "number", helperText: "Maximum value is 100", max: 100 },
    { key: "skip", label: "Skip", type: "number", helperText: "Number of settlements to skip" },
];

export const SETTLEMENTS_COLUMNS: ColumnDef[] = [
    { label: "Settlement ID", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.id}</Box> },
    {
        label: "Status",
        render: (row) => (
            <Chip
                label={row.status}
                size="small"
                sx={{
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: row.status === "processed" ? "#e6f4ea" : row.status === "failed" ? "#fdecea" : "#f5f5f5",
                    color: row.status === "processed" ? "#1e7e34" : row.status === "failed" ? "#c62828" : "#555",
                }}
            />
        ),
    },
    { label: "Amount", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontWeight: 500 }}>{formatAmount(row.amount)}</Typography> },
    { label: "Fees", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatAmount(row.fees)}</Typography> },
    { label: "Tax", render: (row) => <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatAmount(row.tax)}</Typography> },
    { label: "UTR", render: (row) => <Box component="code" sx={{ fontSize: "0.78rem", backgroundColor: "#f5f5f5", p: 0.5, borderRadius: 0.5 }}>{row.utr ?? "—"}</Box> },
    { label: "Currency", render: (row) => <Typography variant="body2">{row.currency}</Typography> },
    { label: "Balance Type", render: (row) => <Typography variant="body2">{row.balance_type ?? "—"}</Typography> },
    { label: "Created At", render: (row) => <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.created_at)}</Typography> },
];

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        maxWidth: '1300px',
        width: '90%',
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2.5),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
}));

const FilterCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
}));

const FilterCardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 460,
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    '& .MuiTableHead-root': {
        backgroundColor: theme.palette.grey[50],
    },
    '& .MuiTableCell-head': {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
}));

const EmptyBox = styled(Box)(({ theme }) => ({
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const RazorpayFilterDialog: React.FC<RazorpayFilterDialogProps> = ({
    open, onClose, title, apiUrl, responseDataPath, columns, filterFields,
}) => {
    const [fieldValues, setFieldValues] = React.useState<Record<string, string>>(
        () => Object.fromEntries(filterFields.map((f) => [f.key, ""]))
    );
    const [rows, setRows] = React.useState<any[]>([]);
    const [totalCount, setTotalCount] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [hasSearched, setHasSearched] = React.useState<boolean>(false);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    React.useEffect(() => {
        setFieldValues(Object.fromEntries(filterFields.map((f) => [f.key, ""])));
    }, [filterFields]);

    function handleSubmit() {
        const params: Record<string, string> = {};
        for (const field of filterFields) {
            const raw = fieldValues[field.key]?.trim() ?? "";
            if (!raw) continue;
            if (field.type === "datetime-local") {
                const unix = toUnixTimestamp(raw);
                if (unix !== undefined) params[field.key] = String(unix);
            } else if (field.type === "number") {
                const num = parseInt(raw, 10);
                if (field.max !== undefined && num > field.max) {
                    toastr.error(`${field.label} cannot be greater than ${field.max}`);
                    return;
                }
                params[field.key] = String(num);
            } else {
                params[field.key] = raw;
            }
        }

        const queryString = new URLSearchParams(params).toString();
        setLoading(true);
        setHasSearched(true);
        setPage(0);

        makeGETRequestOnUserService(`${apiUrl}${queryString ? "?" + queryString : ""}`)
            .then((resp: any) => {
                if (resp && resp.status === 200) {
                    const data = getNestedValue(resp.data, responseDataPath);
                    setRows(data?.items ?? []);
                    setTotalCount(data?.count ?? null);
                } else {
                    toastr.error(`Failed to fetch ${title}`);
                }
            })
            .catch((error: any) => toastr.error(error))
            .finally(() => setLoading(false));
    }

    function handleClose() {
        setFieldValues(Object.fromEntries(filterFields.map((f) => [f.key, ""])));
        setRows([]);
        setTotalCount(null);
        setHasSearched(false);
        setPage(0);
        setRowsPerPage(10);
        onClose();
    }

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Determine grid item size based on number of filter fields
    const getGridItemProps = () => {
        if (filterFields.length === 5) { // ORDERS_FILTER_FIELDS
            return { xs: 12, sm: 6, md: 4, lg: 12 / 5 };
        } else if (filterFields.length === 4) { // SETTLEMENTS_FILTER_FIELDS
            return { xs: 12, sm: 6, md: 6, lg: 3 };
        }
        // Default case
        return { xs: 12, sm: 6, md: 4, lg: 3 };
    };

    const gridItemProps = getGridItemProps();

    return (
        <StyledDialog open={open} onClose={handleClose}>
            <StyledDialogTitle>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>{title}</Typography>
                <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </StyledDialogTitle>

            <StyledDialogContent>
                <FilterCard sx={{ mt: 2 }}>
                    <CardContent sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            {filterFields.map((field) => (
                                <Grid item {...gridItemProps} key={field.key}>
                                    <TextField
                                        label={field.label}
                                        type={field.type ?? "text"}
                                        variant="outlined"
                                        size="small"
                                        value={fieldValues[field.key] ?? ""}
                                        onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                                        InputLabelProps={field.type === "datetime-local" ? { shrink: true } : undefined}
                                        inputProps={field.type === "number" ? { min: 0 } : undefined}
                                        helperText={field.helperText}
                                        fullWidth
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </FilterCard>

                {loading ? (
                    <LoadingBox>
                        <Stack direction="column" alignItems="center" spacing={2}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="textSecondary">Loading data...</Typography>
                        </Stack>
                    </LoadingBox>
                ) : hasSearched && rows.length === 0 ? (
                    <EmptyBox>
                        <Stack direction="column" alignItems="center" spacing={2}>
                            <Typography variant="h6" color="textSecondary">No results found</Typography>
                            <Typography variant="body2" color="textSecondary">Try adjusting your filters</Typography>
                        </Stack>
                    </EmptyBox>
                ) : rows.length > 0 ? (
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                        <StyledTableContainer>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((col) => (
                                            <TableCell key={col.label}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row, idx) => (
                                        <TableRow key={row.id ?? idx} hover>
                                            {columns.map((col) => (
                                                <TableCell key={col.label}>{col.render(row)}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                        <Divider />
                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </Paper>
                ) : null}
            </StyledDialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    {loading ? "Fetching..." : "Submit"}
                </Button>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default RazorpayFilterDialog;